import BaseDrawer from "@stardust/base-drawer";
import { buildProductCard } from "@stardust/product-card-builder";
import { ProductCard } from "@stardust/product-card";
import wishlist from "@stardust/wishlist";

const WISHLIST_DRAWER_EVENTS = {
  open: "wishlist-drawer:open",
  close: "wishlist-drawer:close"
};

/**
 * WishlistDrawer - Slide-in drawer UI for displaying wishlist items
 * 
 * Responsibilities:
 * - Open/close animation and state
 * - Rendering wishlist items as product cards
 * - Remove button UI (delegates actual removal to wishlist module)
 * 
 * Listens for:
 * - wishlist-drawer:open - Opens the drawer
 * - wishlist-drawer:close - Closes the drawer
 * - wishlist:updated - Refreshes items when open
 */
class WishlistDrawer extends BaseDrawer {
  constructor(element) {
    super(element, {
      openClass: 'opacity-0 translate-x-full',
      closeClass: 'opacity-0 translate-x-full',
    });
  }

  load() {
    // Cache DOM
    this.itemsContainer = this.element.querySelector("[data-wishlist-items-container]");
    this.emptyState = this.element.querySelector("[data-wishlist-empty]");
    this.loadingState = this.element.querySelector("[data-wishlist-loading]");

    // Events
    document.addEventListener(WISHLIST_DRAWER_EVENTS.open, this._boundOpen);
    document.addEventListener(WISHLIST_DRAWER_EVENTS.close, this._boundClose);
    document.addEventListener("wishlist:updated", () => this.isOpen && this.loadItems());
  }

  // Hook called when drawer opens
  onOpen() {
    this.loadItems();
  }

  async loadItems() {
    // Concurrent passes race: an early open awaits readyPromise, whose
    // completion fires wishlist:updated, which starts a second pass — both
    // then append into the same container (items doubled). Only the newest
    // pass may touch the DOM; stale passes bail after every await.
    const seq = (this._loadSeq = (this._loadSeq || 0) + 1);
    this._showLoading();

    try {
      // Opened before Swym's SDK finished initializing, the snapshot is still
      // empty — wait for the initial load instead of rendering a false empty
      // state (the loading state above stays visible meanwhile).
      await wishlist.readyPromise;
      if (seq !== this._loadSeq) return;
      const items = wishlist.getUserWishList() || [];

      if (items.length === 0) {
        this._showEmpty();
      } else {
        await this._renderItems(items, seq);
        if (seq !== this._loadSeq) return;
        this._showItems(items.length);
      }
    } catch (error) {
      if (seq !== this._loadSeq) return;
      console.error("[WishlistDrawer] Failed to load:", error);
      this._showEmpty();
    }
  }

  async _renderItems(items, seq) {
    if (!this.itemsContainer) return;

    // Fetch product data
    const products = await wishlist.fetchProducts(items);

    if (seq !== this._loadSeq) return;
    this.itemsContainer.innerHTML = '';

    // Build cards
    products.forEach((product) => {
      if (!product) return;

      const card = buildProductCard(product);

      // Replace the wishlist heart button with a remove button
      const heartBtn = card.querySelector('[data-product-card-wishlist]');
      const removeBtn = this._createRemoveButton(product.handle);
      if (heartBtn) {
        heartBtn.replaceWith(removeBtn);
      } else {
        card.style.position = 'relative';
        card.appendChild(removeBtn);
      }

      this.itemsContainer.appendChild(card);
      new ProductCard(card);
    });
  }

  _createRemoveButton(handle) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'absolute top-2 right-2 z-10 flex cursor-pointer items-center justify-center w-6 h-6 rounded-full bg-white/80 hover:bg-white shadow-sm text-gray-600 hover:text-red-600 transition-colors md:top-4 md:right-4';
    btn.setAttribute('aria-label', 'Remove from wishlist');
    btn.dataset.wishlistRemoveHandle = handle;
    btn.innerHTML = `
      <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
      </svg>
    `;

    btn.addEventListener('click', (e) => {
      e.preventDefault();
      wishlist.removeByHandle(handle);
    });

    return btn;
  }

  // ─────────────────────────────────────────────────────────────
  // UI State Helpers
  // ─────────────────────────────────────────────────────────────

  _showLoading() {
    this.loadingState?.classList.remove("hidden");
    this.emptyState?.classList.add("hidden");
    this.itemsContainer?.classList.add("hidden");
  }

  _showEmpty() {
    this.loadingState?.classList.add("hidden");
    this.emptyState?.classList.remove("hidden");
    this.itemsContainer?.classList.add("hidden");
  }

  _showItems() {
    this.loadingState?.classList.add("hidden");
    this.emptyState?.classList.add("hidden");
    this.itemsContainer?.classList.remove("hidden");
  }
}

// ─────────────────────────────────────────────────────────────
// Init
// ─────────────────────────────────────────────────────────────

const init = () => {
  const wishlistDrawer = document.querySelector('[data-wishlist-drawer]');
  if (wishlistDrawer) new WishlistDrawer(wishlistDrawer).load();
};
init();
